import React, { useState, useEffect } from 'react';
import { Play, Heart, Activity, Calendar, Award, Settings, ChevronLeft, ShieldAlert, TrendingUp, RefreshCw, Footprints, Bluetooth, HelpCircle, Check, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import { showToast } from '../utils/toast';
import { useHeartRate } from '../hooks/useHeartRate';

const GOALS = [
    { key: '1km', label: '1 KM', sub: '4 weken • Speed & Start' },
    { key: '5km', label: '5 KM', sub: '8 weken • Road Runner' },
    { key: '10km', label: '10 KM', sub: '10 weken • Performance' },
    { key: '21.1km', label: '21.1 KM', sub: '12 weken • Halve Marathon' },
    { key: '42.2km', label: '42.2 KM', sub: '16 weken • Marathon' }
];

const SMARTWATCH_GUIDES = [
    {
        brand: 'Garmin (Fenix, Forerunner, Epix, Venu, Instinct)',
        steps: [
            'Houd op je horloge de Menu (Up) knop ingedrukt.',
            'Ga naar Sensoren en accessoires > Polshartslag.',
            'Zet "Zend hartslag uit" (Broadcast Heart Rate) op Aan (of start de activiteit "Virtual Run" voor hartslag + cadans + pace).',
            'Klik hieronder op "Zoek Horloge (Alle)".'
        ]
    },
    {
        brand: 'Polar (Vantage, Pacer, Grit X, Ignite & H9/H10)',
        steps: [
            'Borstband (H9/H10): Draag de band en klik direct op "Koppel HR Band".',
            'Horloge: Open het pre-training scherm (kies bijv. Hardlopen), open het Snelmenu (tandwiel) en kies "HR delen met andere apparaten".',
            'Klik daarna op "Zoek Horloge (Alle)".'
        ]
    },
    {
        brand: 'COROS (Pace 2/3, Apex, Vertix)',
        steps: [
            'Houd de terugknop op je COROS ingedrukt om het Toolbox-menu te openen.',
            'Kies "Hartslag uitzenden" (Broadcast HR) en laat dit scherm actief staan tijdens het koppelen.',
            'Klik op "Zoek Horloge (Alle)".'
        ]
    },
    {
        brand: 'Suunto (Race, Vertical, 9 Peak)',
        steps: [
            'Veeg omlaag of open een sportmodus > Opties.',
            'Zet "Hartslag delen" / Bluetooth HR Broadcast aan en koppel via "Zoek Horloge (Alle)".'
        ]
    },
    {
        brand: 'Wahoo (TICKR) & WHOOP (4.0)',
        steps: [
            'WHOOP 4.0: Open de WHOOP app op je telefoon > Device Settings > zet "Broadcast Heart Rate" aan.',
            'Wahoo TICKR: Maak de elektroden licht vochtig, doe de band om en klik direct op "Koppel HR Band".'
        ]
    },
    {
        brand: 'Samsung Galaxy Watch & Google Pixel Watch (Wear OS)',
        steps: [
            'Standaard staat BLE HR Broadcast uit op Wear OS.',
            'Installeer de gratis Wear OS app "Heart for Bluetooth" of "HR2VP" op je horloge om je hartslag als standaard BLE-signaal uit te zenden.'
        ]
    },
    {
        brand: 'Apple Watch (WatchOS)',
        steps: [
            'Let op: Apple blokkeert standaard rechtstreekse Bluetooth-hartslaguitzending naar webbrowsers (en ondersteunt geen Web Bluetooth in iOS Safari).',
            'Tip voor Apple-gebruikers: Gebruik een losse Bluetooth borstband op Android/Chrome, of gebruik de ingebouwde MYWOD Timer & Streeftempo\'s terwijl je je run meelogt op je Apple Watch.'
        ]
    }
];

const cleanTextSymbols = (str = '') => {
    return String(str || '')
        .replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '')
        .replace(/[\uD800-\uDFFF]/g, '')
        .replace(/[🎯⏱️💓🟢🔥⚡🏔️🛡️🔄🩺🌱🦵🔋🏁📈🌿\uFFFD]/g, '')
        .replace(/[ \t]{2,}/g, ' ')
        .replace(/\n +/g, '\n')
        .trim();
};

export default function RunCoach({ user, api, token, glassClass, setView, setCurrentWod, setupTimerForWod, requestWakeLock }) {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('today');
    const [data, setData] = useState(null);

    const [readiness, setReadiness] = useState('good');
    const [absenceReason, setAbsenceReason] = useState('busy');
    const [livePreviewWod, setLivePreviewWod] = useState(null);
    const [isUpdatingPreview, setIsUpdatingPreview] = useState(false);

    // Smartwatch Hulp State
    const [showBleHelp, setShowBleHelp] = useState(false);
    const [selectedBrandIdx, setSelectedBrandIdx] = useState(0);

    // Uitvouwbare Sessie Details in het Schema Tabblad
    const [expandedRoadmapKey, setExpandedRoadmapKey] = useState(null);
    const [expandedSessionDetails, setExpandedSessionDetails] = useState({});
    const [loadingRoadmapKey, setLoadingRoadmapKey] = useState(null);

    // Setup Form State
    const [goalDistance, setGoalDistance] = useState('5km');
    const [daysPerWeek, setDaysPerWeek] = useState(3);
    const [ref5kMin, setRef5kMin] = useState('');
    const [ref5kSec, setRef5kSec] = useState('');
    const [restingHr, setRestingHr] = useState('');
    const [shoeName, setShoeName] = useState('Standaard Hardloopschoen');

    const hr = useHeartRate(user?.age || 30);

    const fetchRunPlan = async () => {
        setLoading(true);
        try {
            const res = await api.fetch('/running/plan', {}, token);
            if (res) {
                setData(res);
                setLivePreviewWod(res.nextWorkoutPreview);
                if (res.profile) {
                    setGoalDistance(res.profile.goal_distance || '5km');
                    setDaysPerWeek(res.profile.days_per_week || 3);
                    setRestingHr(res.profile.resting_hr || '');
                    setShoeName(res.profile.shoe_name || 'Standaard Hardloopschoen');
                    if (res.profile.reference_5k_seconds) {
                        setRef5kMin(Math.floor(res.profile.reference_5k_seconds / 60));
                        setRef5kSec(res.profile.reference_5k_seconds % 60);
                    }
                }
            }
        } catch (err) {
            showToast('Kon Run Coach gegevens niet ophalen.', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRunPlan();
    }, []);

    const updateSessionByReadiness = async (newReadiness, newAbsenceReason, overrideWeek = null, overrideSession = null) => {
        setIsUpdatingPreview(true);
        try {
            const res = await api.fetch('/running/generate-session', {
                method: 'POST',
                body: JSON.stringify({
                    readiness: newReadiness,
                    absenceReason: newAbsenceReason,
                    overrideWeek,
                    overrideSession
                })
            }, token);
            if (res && res.wod) {
                setLivePreviewWod(res.wod);
            }
        } catch (e) {
            showToast('Kon sessie niet aanpassen.', 'error');
        } finally {
            setIsUpdatingPreview(false);
        }
    };

    // Haal bij klikken in het Schema-overzicht de details op en vouw de dag ter plekke open
    const handleToggleRoadmapSession = async (weekNumber, dayNumber) => {
        const key = `${weekNumber}-${dayNumber}`;
        if (expandedRoadmapKey === key) {
            setExpandedRoadmapKey(null);
            return;
        }
        setExpandedRoadmapKey(key);
        if (expandedSessionDetails[key]) return;

        setLoadingRoadmapKey(key);
        try {
            const res = await api.fetch('/running/generate-session', {
                method: 'POST',
                body: JSON.stringify({
                    readiness: 'good',
                    absenceReason: 'none',
                    overrideWeek: weekNumber,
                    overrideSession: dayNumber
                })
            }, token);
            if (res && res.wod) {
                setExpandedSessionDetails(prev => ({ ...prev, [key]: res.wod }));
            }
        } catch (e) {
            showToast('Kon sessie-details niet laden.', 'error');
        } finally {
            setLoadingRoadmapKey(null);
        }
    };

    const handleReadinessClick = (val) => {
        setReadiness(val);
        const daysAway = data?.runStats?.daysSinceLastRun || 0;
        updateSessionByReadiness(val, daysAway > 6 ? absenceReason : 'none');
    };

    const handleAbsenceReasonClick = (reason) => {
        setAbsenceReason(reason);
        updateSessionByReadiness(readiness, reason);
    };

    const handleSaveSetup = async (e, resetProgress = false) => {
        if (e) e.preventDefault();
        const total5kSec = ref5kMin ? (parseInt(ref5kMin, 10) * 60 + (parseInt(ref5kSec || 0, 10))) : null;
        try {
            await api.fetch('/running/setup', {
                method: 'POST',
                body: JSON.stringify({
                    goal_distance: goalDistance,
                    days_per_week: daysPerWeek,
                    reference_5k_seconds: total5kSec,
                    resting_hr: restingHr ? parseInt(restingHr, 10) : null,
                    shoe_name: shoeName,
                    reset_progress: resetProgress,
                    current_week: data?.profile?.current_week || 1,
                    session_in_week: data?.profile?.session_in_week || 1
                })
            }, token);
            showToast('Schema bijgewerkt.', 'success');
            await fetchRunPlan();
            setActiveTab('today');
        } catch (err) {
            showToast('Opslaan mislukt.', 'error');
        }
    };

    // Start een Run-sessie. Als het een reeds afgeronde sessie is (isReplay = true), hoogt hij na afloop NIET de weekteller op,
    // maar telt de workout wél gewoon mee voor je Run-statistieken, kilometric-grafiek, schoenenslijtage en CNS/spierherstel!
    const handleStartRunSession = async (wodToStart, isReplay = false) => {
        const target = wodToStart || livePreviewWod;
        if (!target) return;

        const cleanDesc = cleanTextSymbols(target.description);
        const cleanTitleStr = cleanTextSymbols(target.title || 'Run Sessie');

        const formattedWod = {
            title: cleanTitleStr,
            type: `Run: ${target.type || 'Hardlopen'}`,
            format: 'Cardio',
            timerMode: 'stopwatch',
            timeCap: 0,
            intensity: 7,
            isFitness: false,
            isRunSession: !isReplay,
            isRunReplay: isReplay,
            targetDistanceKm: target.targetDistanceKm || 5,
            targetPace: target.targetPace,
            targetHrZone: target.targetHrZone,
            preWorkoutAdvice: `Streeftempo: ${target.targetPace}  |  Doelzone: ${target.targetHrZone}`,
            description: cleanDesc
        };

        if (requestWakeLock) await requestWakeLock();
        setCurrentWod(formattedWod);
        setupTimerForWod(formattedWod);
        setView('focus');
    };

    if (loading) {
        return (
            <div className={`${glassClass} flex flex-col items-center justify-center py-16`}>
                <RefreshCw size={32} className="text-orange-500 animate-spin mb-3" />
                <p className="text-xs font-black uppercase tracking-widest text-gray-400">Schema laden...</p>
            </div>
        );
    }

    const profile = data?.profile || {};
    const plan = data?.planOverview || {};
    const runStats = data?.runStats || {};
    const daysAway = runStats.daysSinceLastRun || 0;
    const currentWodPreview = livePreviewWod || data?.nextWorkoutPreview || {};

    const totalSessions = (plan.totalWeeks || 8) * (profile.days_per_week || 3);
    const completedSessionsCount = Math.max(0, ((profile.current_week - 1) * (profile.days_per_week || 3)) + (profile.session_in_week - 1));
    const progressPct = Math.min(100, Math.round((completedSessionsCount / Math.max(1, totalSessions)) * 100));

    const cleanTitle = cleanTextSymbols(currentWodPreview.title || 'Zone 2 Duurloop');
    const cleanPhase = cleanTextSymbols(currentWodPreview.phaseName || 'Opbouwblok');
    const cleanDescription = cleanTextSymbols(currentWodPreview.description || '');

    return (
        <div className="space-y-6 pb-20 animate-fade-in">
            {/* Header & Afstandskiezer */}
            <div className={glassClass}>
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 pb-5 border-b border-zinc-700">
                    <div className="flex items-center gap-3.5">
                        <button
                            type="button"
                            onClick={() => setView('dashboard')}
                            className="p-2.5 bg-zinc-800 hover:bg-zinc-700 rounded-xl border border-zinc-700 text-gray-300 hover:text-white transition-colors"
                            title="Terug naar Dashboard"
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <div>
                            <div className="flex items-center gap-2.5">
                                <span className="text-[10px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded bg-zinc-800 text-orange-500 border border-zinc-700">
                                    RUN PROGRAMMA
                                </span>
                                <span className="text-xs font-bold text-gray-400">
                                    Week {profile.current_week || 1} van {plan.totalWeeks || 8} • Dag {profile.session_in_week || 1}/{profile.days_per_week || 3}
                                </span>
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-black text-white uppercase tracking-tight mt-1">
                                {plan.goalLabel || '5 KM Road Runner'}
                            </h2>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5 w-full lg:w-auto">
                        {GOALS.map(g => {
                            const active = profile.goal_distance === g.key;
                            return (
                                <button
                                    key={g.key}
                                    type="button"
                                    onClick={async () => {
                                        setGoalDistance(g.key);
                                        await api.fetch('/running/setup', {
                                            method: 'POST',
                                            body: JSON.stringify({
                                                ...profile,
                                                goal_distance: g.key,
                                                reset_progress: true
                                            })
                                        }, token);
                                        showToast(`Doel ingesteld op ${g.label}`, 'success');
                                        fetchRunPlan();
                                    }}
                                    className={`flex-1 sm:flex-initial px-3.5 py-2 rounded-lg text-xs font-black uppercase tracking-wider border transition-all ${
                                        active
                                            ? 'bg-orange-600 text-white border-orange-500 shadow-sm'
                                            : 'bg-zinc-800 text-gray-300 border-zinc-700 hover:border-orange-500/50'
                                    }`}
                                >
                                    {g.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Progressiebalk */}
                <div className="mt-4">
                    <div className="flex justify-between items-center text-xs font-bold mb-1.5">
                        <span className="text-gray-400 uppercase tracking-wider">
                            Schema Voortgang ({completedSessionsCount} van {totalSessions} trainingen afgerond)
                        </span>
                        <span className="text-white font-black tabular-nums">{progressPct}%</span>
                    </div>
                    <div className="w-full h-2.5 bg-zinc-800 rounded-full overflow-hidden border border-zinc-700">
                        <div
                            className="h-full bg-orange-500 transition-all duration-500"
                            style={{ width: `${progressPct}%` }}
                        ></div>
                    </div>
                </div>

                {/* Sub-Tabs */}
                <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-zinc-800">
                    {[
                        { id: 'today', label: 'Training Vandaag', icon: <Play size={14}/> },
                        { id: 'roadmap', label: `Schema (${plan.totalWeeks} Wkn)`, icon: <Calendar size={14}/> },
                        { id: 'stats', label: 'Zones & Tempo', icon: <TrendingUp size={14}/> },
                        { id: 'settings', label: 'Instellingen', icon: <Settings size={14}/> }
                    ].map(t => (
                        <button
                            key={t.id}
                            type="button"
                            onClick={() => setActiveTab(t.id)}
                            className={`px-4 py-2.5 rounded-lg text-xs font-black uppercase tracking-wider flex items-center gap-2 border transition-all ${
                                activeTab === t.id
                                    ? 'bg-orange-600 text-white border-orange-500 shadow-sm'
                                    : 'bg-zinc-800 text-gray-400 border-zinc-700 hover:border-orange-500/50'
                            }`}
                        >
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* TAB 1: VANDAAG, SMARTWATCH KOPPELING & CHECK-IN */}
            {activeTab === 'today' && (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className={`${glassClass} lg:col-span-5 space-y-6`}>
                        {/* Wearable & Smartwatch Hulp Paneel */}
                        <div className="p-4 rounded-xl bg-zinc-800/40 border border-zinc-700 space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Bluetooth size={16} className="text-blue-500" />
                                    <span className="text-xs font-black uppercase tracking-wider text-white">
                                        Wearable & Hartslag Sensor
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowBleHelp(!showBleHelp)}
                                    className="text-xs font-bold text-blue-500 hover:underline flex items-center gap-1"
                                >
                                    <HelpCircle size={14}/> Smartwatch Hulp
                                </button>
                            </div>

                            {showBleHelp && (
                                <div className="p-3.5 rounded-xl bg-zinc-900 border border-zinc-700 text-xs space-y-3 animate-fade-in">
                                    <div>
                                        <label className="text-[10px] font-black uppercase tracking-wider text-gray-400 block mb-1.5">
                                            Kies jouw merk horloge of band:
                                        </label>
                                        <select
                                            value={selectedBrandIdx}
                                            onChange={(e) => setSelectedBrandIdx(parseInt(e.target.value, 10))}
                                            className="w-full bg-zinc-800 border border-zinc-600 rounded-lg p-2 text-xs font-bold text-white outline-none focus:border-orange-500"
                                        >
                                            {SMARTWATCH_GUIDES.map((g, idx) => (
                                                <option key={idx} value={idx}>{g.brand}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <ol className="list-decimal pl-4 space-y-1 text-[11px] text-gray-300 leading-relaxed">
                                        {SMARTWATCH_GUIDES[selectedBrandIdx].steps.map((stepText, sIdx) => (
                                            <li key={sIdx}>{stepText}</li>
                                        ))}
                                    </ol>
                                </div>
                            )}

                            {!hr.isConnected ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                    <button
                                        type="button"
                                        onClick={() => hr.connectHeartRate(false)}
                                        disabled={hr.isConnecting}
                                        className="py-2.5 px-3 bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-700 hover:border-red-500/50 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-colors"
                                    >
                                        <Heart size={14} className="text-red-500"/>
                                        <span>{hr.isConnecting ? 'Zoeken...' : 'Koppel HR Band'}</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => hr.connectHeartRate(true)}
                                        disabled={hr.isConnecting}
                                        className="py-2.5 px-3 bg-blue-600 hover:bg-blue-500 text-white border border-blue-500 rounded-lg text-xs font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
                                        title="Toont alle Bluetooth-apparaten in de buurt voor sporthorloges"
                                    >
                                        <Bluetooth size={14} />
                                        <span>Zoek Horloge (Alle)</span>
                                    </button>
                                </div>
                            ) : (
                                <div className="p-3 rounded-lg bg-zinc-900 border border-emerald-500/40 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <Heart size={20} className="text-red-500 fill-red-500 animate-pulse"/>
                                        <div>
                                            <div className="text-sm font-black text-white tabular-nums">
                                                {hr.currentBpm || '--'} BPM <span className="text-xs font-normal text-gray-400">({hr.deviceName})</span>
                                            </div>
                                            <div className={`text-[10px] font-bold uppercase ${hr.zoneInfo.color}`}>
                                                {hr.zoneInfo.name} {hr.liveCadence ? `• ${hr.liveCadence} SPM` : ''}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={hr.disconnectHeartRate}
                                        className="text-[11px] font-bold text-gray-400 hover:text-white px-2.5 py-1 rounded bg-zinc-800 border border-zinc-700"
                                    >
                                        Loskoppelen
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Fysieke Status Check-in (Helder contrast in zowel Light als Dark mode!) */}
                        <div>
                            <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-2.5">
                                Fysieke Status Check-in
                            </h3>
                            <div className="space-y-2.5">
                                {[
                                    { key: 'good', dot: 'bg-emerald-500', title: 'Fris & Volledig Belastbaar', desc: 'Standaard opbouw en geplande intensiteit.' },
                                    { key: 'heavy_legs', dot: 'bg-amber-500', title: 'Zware Benen (Na Krachttraining)', desc: 'Volume -15% en strikte Zone 2 begrenzing.' },
                                    { key: 'low_energy', dot: 'bg-red-500', title: 'Vermoeid / Weinig Herstel', desc: 'Volume -25% en focus op rustige techniek.' }
                                ].map(opt => {
                                    const isSelected = readiness === opt.key;
                                    return (
                                        <button
                                            key={opt.key}
                                            type="button"
                                            onClick={() => handleReadinessClick(opt.key)}
                                            className={`w-full text-left p-3.5 rounded-xl border transition-all flex items-center justify-between gap-3 ${
                                                isSelected
                                                    ? 'bg-zinc-900 border-2 border-orange-500 shadow-sm'
                                                    : 'bg-zinc-800/40 border border-zinc-700 hover:border-orange-500/40'
                                            }`}
                                        >
                                            <div className="flex items-start gap-3">
                                                <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${opt.dot}`}></span>
                                                <div>
                                                    <div className="font-bold text-white text-sm">{opt.title}</div>
                                                    <div className="text-xs text-gray-400 mt-0.5">{opt.desc}</div>
                                                </div>
                                            </div>
                                            {isSelected && <Check size={16} className="text-orange-500 shrink-0"/>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {daysAway > 6 && (
                            <div className="p-4 rounded-xl bg-zinc-800/40 border border-amber-500/40 space-y-3">
                                <div className="flex items-start gap-2.5">
                                    <ShieldAlert size={18} className="text-amber-500 shrink-0 mt-0.5"/>
                                    <div>
                                        <h4 className="text-xs font-black uppercase tracking-wider text-amber-500">
                                            Laatste geregistreerde run: {daysAway} dagen geleden
                                        </h4>
                                        <p className="text-xs text-gray-300 mt-0.5">
                                            Selecteer de reden van de pauze om pees- en hartbelasting veilig te hervatten:
                                        </p>
                                    </div>
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { key: 'busy', label: 'Drukte / Werk' },
                                        { key: 'illness', label: 'Ziekte / Griep' },
                                        { key: 'injury', label: 'Pijn / Blessure' }
                                    ].map(r => (
                                        <button
                                            key={r.key}
                                            type="button"
                                            onClick={() => handleAbsenceReasonClick(r.key)}
                                            className={`py-2 px-2 rounded-lg text-xs font-bold text-center border transition-all ${
                                                absenceReason === r.key
                                                    ? 'bg-orange-600 text-white border-orange-500'
                                                    : 'bg-zinc-900 text-gray-300 border-zinc-700'
                                            }`}
                                        >
                                            {r.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Rechterkolom: Sessie Details */}
                    <div className={`${glassClass} lg:col-span-7 flex flex-col justify-between`}>
                        <div>
                            <div className="flex items-center justify-between gap-2 mb-4 pb-3 border-b border-zinc-700">
                                <div>
                                    <span className="text-[10px] font-black uppercase tracking-widest text-orange-500 block">
                                        {cleanPhase} • Week {currentWodPreview.weekNumber || profile.current_week}
                                    </span>
                                    <h3 className="text-xl sm:text-2xl font-black text-white mt-0.5">
                                        {cleanTitle}
                                    </h3>
                                </div>
                                {isUpdatingPreview && <RefreshCw size={18} className="text-orange-500 animate-spin"/>}
                            </div>

                            <div className="grid grid-cols-3 gap-3 mb-5">
                                <div className="p-3.5 rounded-xl bg-zinc-800/40 border border-zinc-700">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-gray-400 block">Afstand</span>
                                    <span className="text-lg sm:text-2xl font-black text-white tabular-nums">{currentWodPreview.targetDistanceKm || 5} km</span>
                                </div>
                                <div className="p-3.5 rounded-xl bg-zinc-800/40 border border-zinc-700">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-gray-400 block">Streeftempo</span>
                                    <span className="text-base sm:text-xl font-black text-orange-500 tabular-nums">{currentWodPreview.targetPace || '6:00 /km'}</span>
                                </div>
                                <div className="p-3.5 rounded-xl bg-zinc-800/40 border border-zinc-700">
                                    <span className="text-[10px] font-black uppercase tracking-wider text-gray-400 block">Hartslagzone</span>
                                    <span className="text-xs sm:text-base font-black text-emerald-500 tabular-nums">{currentWodPreview.targetHrZone || 'Zone 2'}</span>
                                </div>
                            </div>

                            <div className="p-4 rounded-xl bg-zinc-800/40 border border-zinc-700 mb-6">
                                <h4 className="text-xs font-black uppercase tracking-wider text-gray-400 mb-2">Sessie Opbouw</h4>
                                <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-gray-200">
                                    {cleanDescription}
                                </pre>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-zinc-700 flex flex-col sm:flex-row gap-3">
                            <button
                                type="button"
                                onClick={() => handleStartRunSession(currentWodPreview, false)}
                                className="flex-1 py-4 bg-orange-600 hover:bg-orange-500 text-white font-black text-sm sm:text-base uppercase tracking-widest rounded-xl shadow-md flex items-center justify-center gap-2 transition-colors"
                            >
                                <Play size={18} fill="currentColor"/> Start Run Sessie
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 2: VOLLEDIG SCHEMA OVERZICHT MET UITVOUWBARE DAG-DETAILS & START/HERHAAL KNOP */}
            {activeTab === 'roadmap' && (
                <div className={`${glassClass} space-y-6`}>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pb-4 border-b border-zinc-700">
                        <div>
                            <h3 className="text-lg font-black text-white uppercase tracking-wide">
                                Volledig Schema ({plan.goalLabel})
                            </h3>
                            <p className="text-xs text-gray-400">
                                Klik op een dag om het volledige trainingsblok uit te vouwen en die specifieke sessie te starten of te herhalen.
                            </p>
                        </div>
                        <span className="text-xs font-bold px-3 py-1.5 rounded-lg bg-zinc-800 text-gray-300 border border-zinc-700">
                            Actief: Week {profile.current_week} • Dag {profile.session_in_week}
                        </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                        {(plan.weeks || []).map(wk => {
                            const isCurrentWk = wk.weekNumber === profile.current_week;
                            const isCompletedWk = wk.weekNumber < profile.current_week;
                            const cleanWkPhase = cleanTextSymbols(wk.phaseName || '');

                            return (
                                <div
                                    key={wk.weekNumber}
                                    className={`p-4 rounded-xl border transition-all ${
                                        isCurrentWk
                                            ? 'bg-zinc-800/50 border-2 border-orange-500'
                                            : isCompletedWk
                                            ? 'bg-zinc-900/40 border-emerald-500/40'
                                            : 'bg-zinc-800/30 border-zinc-700'
                                    }`}
                                >
                                    <div className="flex justify-between items-center mb-3 pb-2 border-b border-zinc-700/60">
                                        <div className="flex items-center gap-2">
                                            <span className={`text-sm font-black uppercase ${isCurrentWk ? 'text-orange-500' : 'text-white'}`}>
                                                Week {wk.weekNumber}
                                            </span>
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-zinc-900 text-gray-400 border border-zinc-700">
                                                {cleanWkPhase}
                                            </span>
                                        </div>
                                        <span className="text-xs font-black text-gray-300 tabular-nums">
                                            {wk.totalWeekKm} km
                                        </span>
                                    </div>

                                    <div className="space-y-2.5">
                                        {wk.sessions.map(s => {
                                            const itemKey = `${wk.weekNumber}-${s.day}`;
                                            const isExpanded = expandedRoadmapKey === itemKey;
                                            const isCurrentSess = isCurrentWk && s.day === profile.session_in_week;
                                            const isDoneSess = isCompletedWk || (isCurrentWk && s.day < profile.session_in_week);
                                            const cleanSessType = cleanTextSymbols(s.type || '');
                                            const detailObj = expandedSessionDetails[itemKey];

                                            return (
                                                <div
                                                    key={s.day}
                                                    className={`rounded-xl border transition-all overflow-hidden ${
                                                        isCurrentSess
                                                            ? 'bg-zinc-900 border-orange-500'
                                                            : isDoneSess
                                                            ? 'bg-zinc-900/70 border-emerald-500/40'
                                                            : 'bg-zinc-900/80 border-zinc-700'
                                                    }`}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleRoadmapSession(wk.weekNumber, s.day)}
                                                        className="w-full p-3 flex items-center justify-between gap-2 text-left hover:bg-zinc-800/40 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-2.5 min-w-0">
                                                            <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${
                                                                isDoneSess
                                                                    ? 'bg-emerald-600 text-white border-emerald-500'
                                                                    : isCurrentSess
                                                                    ? 'bg-orange-600 text-white border-orange-500'
                                                                    : 'bg-zinc-800 text-gray-300 border-zinc-700'
                                                            }`}>
                                                                {isDoneSess ? '✓ Voltooid' : `Dag ${s.day}`}
                                                            </span>
                                                            <span className="text-xs font-bold text-white truncate">{cleanSessType}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2 shrink-0">
                                                            <div className="text-right">
                                                                <span className="text-xs font-black text-white block tabular-nums">{s.distanceKm} km</span>
                                                                <span className="text-[10px] text-gray-400 block tabular-nums">{s.pace} ({s.zone})</span>
                                                            </div>
                                                            {isExpanded ? <ChevronUp size={16} className="text-orange-500"/> : <ChevronDown size={16} className="text-gray-400"/>}
                                                        </div>
                                                    </button>

                                                    {isExpanded && (
                                                        <div className="p-3.5 pt-2 border-t border-zinc-800 bg-zinc-800/30 space-y-3 animate-fade-in">
                                                            {loadingRoadmapKey === itemKey ? (
                                                                <div className="py-4 text-center text-xs text-gray-400 flex items-center justify-center gap-2">
                                                                    <RefreshCw size={14} className="animate-spin text-orange-500"/> Details laden...
                                                                </div>
                                                            ) : detailObj ? (
                                                                <>
                                                                    <div className="grid grid-cols-3 gap-2 text-center">
                                                                        <div className="p-2 rounded-lg bg-zinc-900 border border-zinc-700">
                                                                            <span className="text-[9px] uppercase font-bold text-gray-400 block">Afstand</span>
                                                                            <span className="text-xs font-black text-white">{detailObj.targetDistanceKm} km</span>
                                                                        </div>
                                                                        <div className="p-2 rounded-lg bg-zinc-900 border border-zinc-700">
                                                                            <span className="text-[9px] uppercase font-bold text-gray-400 block">Streeftempo</span>
                                                                            <span className="text-xs font-black text-orange-500">{detailObj.targetPace}</span>
                                                                        </div>
                                                                        <div className="p-2 rounded-lg bg-zinc-900 border border-zinc-700">
                                                                            <span className="text-[9px] uppercase font-bold text-gray-400 block">Doelzone</span>
                                                                            <span className="text-[11px] font-black text-emerald-500">{detailObj.targetHrZone}</span>
                                                                        </div>
                                                                    </div>

                                                                    <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-gray-300 bg-zinc-900 p-3 rounded-lg border border-zinc-700">
                                                                        {cleanTextSymbols(detailObj.description)}
                                                                    </pre>

                                                                    <div className="flex gap-2">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleStartRunSession(detailObj, isDoneSess)}
                                                                            className={`flex-1 py-2.5 px-3 rounded-lg font-black text-xs uppercase tracking-wider flex items-center justify-center gap-1.5 text-white transition-all shadow-sm ${
                                                                                isDoneSess ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-orange-600 hover:bg-orange-500'
                                                                            }`}
                                                                        >
                                                                            {isDoneSess ? (
                                                                                <><RotateCcw size={14}/> Dag {s.day} Opnieuw Doen</>
                                                                            ) : (
                                                                                <><Play size={14} fill="currentColor"/> Start Week {wk.weekNumber} • Dag {s.day}</>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                </>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* TAB 3: HARTSLAGZONES & STREEFTEMPO'S */}
            {activeTab === 'stats' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className={`${glassClass} space-y-4`}>
                        <div className="border-b border-zinc-700 pb-3">
                            <h3 className="text-base font-black text-white uppercase flex items-center gap-2">
                                <Heart size={16} className="text-red-500"/> Hartslagzones (Tanaka)
                            </h3>
                            <p className="text-xs text-gray-400">
                                Leeftijd {user.age}j • Geschatte Max HR: <strong>{plan.maxHr} BPM</strong>
                            </p>
                        </div>

                        <div className="space-y-2">
                            {Object.entries(plan.hrZones || {}).map(([zKey, zObj]) => (
                                <div key={zKey} className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-700 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="px-2.5 py-1 rounded text-xs font-black bg-zinc-900 text-orange-500 border border-zinc-700">
                                            {zKey}
                                        </span>
                                        <span className="text-sm font-bold text-white">{zObj.name}</span>
                                    </div>
                                    <span className="text-sm font-black tabular-nums text-white">
                                        {zObj.min} – {zObj.max} BPM
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={`${glassClass} space-y-5`}>
                        <div className="border-b border-zinc-700 pb-3">
                            <h3 className="text-base font-black text-white uppercase flex items-center gap-2">
                                <Award size={16} className="text-orange-500"/> Verwachte Eindtijden & Schoenen
                            </h3>
                            <p className="text-xs text-gray-400">
                                Berekend op basis van je ingestelde 5km-referentietempo.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                            {Object.entries(plan.predictions || {}).map(([dist, timeStr]) => (
                                <div key={dist} className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-700 text-center">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-gray-400 block">{dist}</span>
                                    <span className="text-base font-black text-white tabular-nums">{timeStr}</span>
                                </div>
                            ))}
                            <div className="p-3 rounded-lg bg-zinc-800/40 border border-zinc-700 text-center">
                                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-500 block">Totaal Gelopen</span>
                                <span className="text-base font-black text-white tabular-nums">{runStats.totalLoggedKm || 0} km</span>
                            </div>
                        </div>

                        <div className="p-4 rounded-xl bg-zinc-800/40 border border-zinc-700 space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-xs font-bold text-white flex items-center gap-2">
                                    <Footprints size={15} className="text-orange-500"/> {profile.shoe_name || 'Standaard Hardloopschoen'}
                                </span>
                                <span className="text-xs font-black text-white tabular-nums">
                                    {profile.shoe_km || 0} / 750 km
                                </span>
                            </div>
                            <div className="w-full h-2 bg-zinc-900 rounded-full overflow-hidden border border-zinc-700">
                                <div
                                    className={`h-full ${parseFloat(profile.shoe_km) > 650 ? 'bg-red-500' : 'bg-orange-500'}`}
                                    style={{ width: `${Math.min(100, Math.round(((parseFloat(profile.shoe_km) || 0) / 750) * 100))}%` }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* TAB 4: INSTELLINGEN */}
            {activeTab === 'settings' && (
                <form onSubmit={(e) => handleSaveSetup(e, false)} className={`${glassClass} max-w-2xl mx-auto space-y-5`}>
                    <h3 className="text-lg font-black text-white uppercase tracking-wide border-b border-zinc-700 pb-3">
                        Schema Instellingen
                    </h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-2">Doelafstand</label>
                            <select
                                value={goalDistance}
                                onChange={(e) => setGoalDistance(e.target.value)}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white font-bold outline-none focus:border-orange-500"
                            >
                                {GOALS.map(g => (
                                    <option key={g.key} value={g.key}>{g.label} — {g.sub}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-2">Loopdagen per week</label>
                            <select
                                value={daysPerWeek}
                                onChange={(e) => setDaysPerWeek(parseInt(e.target.value, 10))}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white font-bold outline-none focus:border-orange-500"
                            >
                                <option value={2}>2 Dagen / week</option>
                                <option value={3}>3 Dagen / week</option>
                                <option value={4}>4 Dagen / week</option>
                            </select>
                        </div>

                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-2">
                                Huidige 5 KM Tijd (Min : Sec)
                            </label>
                            <div className="flex gap-2">
                                <input
                                    type="number"
                                    placeholder="Min (bijv. 28)"
                                    value={ref5kMin}
                                    onChange={(e) => setRef5kMin(e.target.value)}
                                    className="w-1/2 bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white font-bold outline-none focus:border-orange-500"
                                />
                                <input
                                    type="number"
                                    placeholder="Sec (bijv. 30)"
                                    value={ref5kSec}
                                    onChange={(e) => setRef5kSec(e.target.value)}
                                    className="w-1/2 bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white font-bold outline-none focus:border-orange-500"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-2">
                                Rusthartslag (BPM, optioneel)
                            </label>
                            <input
                                type="number"
                                placeholder="Bijv. 56"
                                value={restingHr}
                                onChange={(e) => setRestingHr(e.target.value)}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white font-bold outline-none focus:border-orange-500"
                            />
                        </div>

                        <div className="sm:col-span-2">
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-2">
                                Hardloopschoenen
                            </label>
                            <input
                                type="text"
                                value={shoeName}
                                onChange={(e) => setShoeName(e.target.value)}
                                placeholder="Bijv. Nike Pegasus"
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-xl p-3 text-white font-bold outline-none focus:border-orange-500"
                            />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-zinc-700">
                        <button
                            type="submit"
                            className="flex-1 py-3.5 bg-orange-600 hover:bg-orange-500 text-white font-black uppercase tracking-widest rounded-xl transition-all text-sm"
                        >
                            Opslaan
                        </button>
                        <button
                            type="button"
                            onClick={(e) => handleSaveSetup(e, true)}
                            className="px-5 py-3.5 bg-zinc-800 hover:bg-zinc-700 text-gray-300 font-bold text-xs uppercase tracking-wider rounded-xl border border-zinc-700"
                        >
                            Reset naar Week 1, Dag 1
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
